import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, CircleCheckBig, Clock, Headphones, Mail, UserRound } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import Swal from 'sweetalert2';
import axiosInstance from '../api/AxiosInstance';
import Pagination from '../components/Pagination';
import { formatDate, formatTime } from '../helpers/DateFormatter';
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';
import { GetProblemReportsAPI } from '../redux/actions/support/GetProblemReportsAction';
import { ResolveReportAPI } from '../redux/actions/support/ResolveReportAction';
import { resolveReportReset } from '../redux/slices/support/ResolveReportSlice';
import { endpoints } from '../services/endpoints';

const SupportManagement = () => {
    const dispatch = useDispatch();

    const { response, loading, error } = useSelector((state) => state.problemReports);
    const {
        response: resolveResponse,
        loading: resolveLoading,
        error: resolveError,
    } = useSelector((state) => state.resolveReport);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [status, setStatus] = useState('');
    const [reports, setReports] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);
    const [resolvedCount, setResolvedCount] = useState(0);
    const [allTotal, setAllTotal] = useState(0);
    const [resolvingId, setResolvingId] = useState(null);

    const fetchCounts = useCallback(async () => {
        try {
            const [allRes, pendingRes, resolvedRes] = await Promise.all([
                axiosInstance.get(`${endpoints.problemReports}?page=1&limit=1`),
                axiosInstance.get(`${endpoints.problemReports}?page=1&limit=1&status=pending`),
                axiosInstance.get(`${endpoints.problemReports}?page=1&limit=1&status=resolved`),
            ]);
            setAllTotal(allRes?.data?.data?.total || 0);
            setPendingCount(pendingRes?.data?.data?.total || 0);
            setResolvedCount(resolvedRes?.data?.data?.total || 0);
        } catch (err) {
            // Counts are secondary; list still loads via Redux
        }
    }, []);

    const fetchReports = useCallback(() => {
        dispatch(GetProblemReportsAPI(currentPage, limit, status));
    }, [currentPage, dispatch, limit, status]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    useEffect(() => {
        if (response?.status === true) {
            setReports(response?.data?.reports || []);
            setTotalPages(response?.data?.totalPages || 1);
            setTotal(response?.data?.total || 0);
            setCurrentPage(response?.data?.page || 1);
        }
    }, [response]);

    useEffect(() => {
        if (resolveResponse?.status === true) {
            showSuccessToast(resolveResponse?.message || 'Report marked as resolved');
            dispatch(resolveReportReset());
            setResolvingId(null);
            fetchReports();
            fetchCounts();
        }
    }, [dispatch, fetchCounts, fetchReports, resolveResponse]);

    useEffect(() => {
        if (resolveError) {
            showErrorToast(resolveError?.data || 'Unable to resolve report');
            dispatch(resolveReportReset());
            setResolvingId(null);
        }
    }, [dispatch, resolveError]);

    const handleResolve = async (id) => {
        const result = await Swal.fire({
            title: 'Mark as resolved?',
            text: 'This will close the support query for this user.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, resolve',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        setResolvingId(id);
        dispatch(ResolveReportAPI(id));
    };

    const handleStatusFilter = (value) => {
        setStatus(value);
        setCurrentPage(1);
    };

    return (
        <section className="main-section common_section spacer">
            <div className="container">
                <div className="row gy-4">
                    <div className="col-12">
                        <div className="row gx-4 gy-4">
                            <div className="col-sm-6 col-md-4">
                                <div className="dash-num-info-cards">
                                    <div className="dash-num-info-cards-header">
                                        <div className="num-info-content-box">
                                            <h2>Total Queries</h2>
                                            <span className="num-values">{status ? total : allTotal}</span>
                                        </div>
                                        <div className="num-info-icon-box">
                                            <Headphones />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-6 col-md-4">
                                <div className="dash-num-info-cards">
                                    <div className="dash-num-info-cards-header">
                                        <div className="num-info-content-box">
                                            <h2>Pending</h2>
                                            <span className="num-values">{pendingCount}</span>
                                        </div>
                                        <div className="num-info-icon-box">
                                            <Clock />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-6 col-md-4">
                                <div className="dash-num-info-cards">
                                    <div className="dash-num-info-cards-header">
                                        <div className="num-info-content-box">
                                            <h2>Resolved</h2>
                                            <span className="num-values">{resolvedCount}</span>
                                        </div>
                                        <div className="num-info-icon-box">
                                            <CircleCheckBig />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="common-table-wrapper">
                            <div className="common-table-filter-wrapper">
                                <div className="common-left-blk">
                                    <h2 className="fs-5 fw-bold mb-0">Support Queries</h2>
                                    <p className="mb-0 mt-1 text-muted fs-14">
                                        User problem reports submitted from the app
                                    </p>
                                </div>
                                <div className="common-right-blk">
                                    <div className="common-sort-blk d-flex flex-wrap gap-3 align-items-center">
                                        <label className="d-flex align-items-center gap-2 mb-0">
                                            Status:
                                            <select
                                                className="form-select"
                                                value={status}
                                                onChange={(event) => handleStatusFilter(event.target.value)}
                                            >
                                                <option value="">All</option>
                                                <option value="pending">Pending</option>
                                                <option value="resolved">Resolved</option>
                                            </select>
                                        </label>
                                        <label className="d-flex align-items-center gap-2 mb-0">
                                            Show:
                                            <select
                                                className="form-select"
                                                value={limit}
                                                onChange={(event) => {
                                                    setLimit(Number(event.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={15}>15</option>
                                                <option value={20}>20</option>
                                            </select>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {loading && reports.length === 0 ? (
                                <div style={{ minHeight: '260px' }} className="d-flex align-items-center justify-content-center">
                                    <PulseLoader size={18} color="#0f0a30" />
                                </div>
                            ) : error ? (
                                <div style={{ minHeight: '260px' }} className="d-flex align-items-center justify-content-center">
                                    <p className="text-danger mb-0">{error?.data || 'Something went wrong'}</p>
                                </div>
                            ) : reports.length > 0 ? (
                                <>
                                    {reports.map((report) => (
                                        <div className="reported_content_block" key={report._id}>
                                            <div className="reported_content_item">
                                                <div className="reported_content_header hstack flex-wrap align-content-start gap-3">
                                                    <div className="reported_user_img d-flex align-items-center justify-content-center bg-light">
                                                        <UserRound size={22} />
                                                    </div>
                                                    <div className="reported_user_detail">
                                                        <div className="hstack flex-wrap align-content-start gap-3">
                                                            <span className="fw-medium">{report.name}</span>
                                                            <span className="reported_on">
                                                                {report.createdAt ? `${formatDate(report.createdAt)} at ${formatTime(report.createdAt)}` : ''}
                                                            </span>
                                                            <span
                                                                className="reported_badge"
                                                                style={{
                                                                    background: report.status === 'resolved' ? '#198754' : '#d97706',
                                                                }}
                                                            >
                                                                {report.status === 'resolved' ? 'Resolved' : 'Pending'}
                                                            </span>
                                                        </div>
                                                        <div className="reported_reason_content mt-1">
                                                            <span className="d-inline-flex align-items-center gap-1">
                                                                <Mail size={14} /> {report.email}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="reported_post">
                                                    {report.subject && (
                                                        <p className="fs-14 fw-semibold mb-1">{report.subject}</p>
                                                    )}
                                                    <p className="fs-14 fw-semibold mb-1">Message</p>
                                                    <p className="fs-14 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {report.message}
                                                    </p>
                                                    {report.status === 'resolved' && report.resolvedAt && (
                                                        <p className="fs-14 text-muted mb-0 mt-2">
                                                            Resolved on {formatDate(report.resolvedAt)} at {formatTime(report.resolvedAt)}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="reported_post_action_block">
                                                    <button
                                                        className="reported_post_action_btn"
                                                        disabled={report.status === 'resolved' || (resolveLoading && resolvingId === report._id)}
                                                        onClick={() => handleResolve(report._id)}
                                                    >
                                                        {resolveLoading && resolvingId === report._id ? (
                                                            <PulseLoader color="#000000" size={8} />
                                                        ) : (
                                                            <>
                                                                <span><CheckCircle2 /></span>
                                                                <span>{report.status === 'resolved' ? 'Resolved' : 'Mark Resolved'}</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <div className="mt-3">
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            items={total}
                                            limit={limit}
                                            handlePageChange={setCurrentPage}
                                        />
                                    </div>
                                </>
                            ) : (
                                <h2 className="text-center py-5">No support queries found</h2>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SupportManagement;
